import { useEffect, useState } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import voiceCopy from '@/i18n/ns/voice';
import {
  AccountDeletionSection,
  AppearanceField,
  LogoutEverywhereSection,
  PersonalityField,
  TimezoneField,
} from './personal-settings-fields';
import { PasswordChangeSection } from '@/components/password-change-section';
import { MemoriesSection } from '@/components/memories-section';
import { ModelPicker } from '@/components/model-picker';
import {
  LanguagePicker,
  languagePickerSession,
  languageSettingValue,
} from '@/components/language-picker';
import {
  fetchServerSettings,
  putServerSettings,
  type Settings,
  type ThemeChoice,
} from '@/settings';
import type { SessionUser } from '@/server-threads';
import { VoiceSettingsSection } from '@/voice/settings-section';
import { VoiceSignatureSection } from './voice-signature-section';
import { useAppCopy } from '@/i18n/context';
import companySettingsCopy from '@/i18n/ns/company-settings';

export function PersonalSettings({
  section,
  user,
  onSaved,
  onPasswordChanged,
  onLoggedOutEverywhere,
  onAccountDeleted,
  onEditStateChange,
  themeChoice = 'light',
  onChooseTheme,
}: {
  section: string;
  user: SessionUser | null;
  onSaved: (settings: Settings) => void;
  onPasswordChanged: () => void;
  onLoggedOutEverywhere: () => void;
  onAccountDeleted: () => void;
  onEditStateChange: (state: { dirty: boolean; busy: boolean }) => void;
  themeChoice?: ThemeChoice;
  onChooseTheme?: (choice: ThemeChoice) => void | Promise<void>;
}) {
  const copy = useNamespaceCopy(voiceCopy);
  const appCopy = useAppCopy();
  const companyCopy = useNamespaceCopy(companySettingsCopy);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [initialSettings, setInitialSettings] = useState<Settings | null>(null);
  const [initialLanguage, setInitialLanguage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const load = async () => {
    setError(null);
    setSettings(null);
    const value = await fetchServerSettings();
    if (!value) {
      setError(appCopy.settingsSaveFailed);
      return;
    }
    const language = languagePickerSession(value.language);
    setInitialLanguage(language.initialValue);
    const next = { ...value, language: language.value };
    setSettings(next);
    setInitialSettings(next);
  };
  useEffect(() => {
    void load();
  }, [user?.id]);
  useEffect(() => {
    onEditStateChange({
      dirty: Boolean(
        settings && initialSettings && JSON.stringify(settings) !== JSON.stringify(initialSettings),
      ),
      busy,
    });
  }, [settings, initialSettings, busy, onEditStateChange]);
  const update = (key: keyof Settings, value: string | boolean) => {
    setSettings((previous) => previous && { ...previous, [key]: value });
    setSaved(false);
  };
  const save = async () => {
    if (!settings) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    // Only this form's own fields: the theme is saved by the Appearance
    // control (or by asking in chat) and a save here never sends or clears it.
    const next = {
      language: languageSettingValue(settings.language ?? ''),
      timezone: settings.timezone || null,
      personality: settings.personality?.trim() || null,
    };
    try {
      onSaved(await putServerSettings(next));
      setInitialSettings(settings);
      setSaved(true);
    } catch {
      setError(appCopy.settingsSaveFailed);
    } finally {
      setBusy(false);
    }
  };
  if (section === 'account')
    return (
      <>
        <h1>{appCopy.account}</h1>
        <p className="company-description">{appCopy.accountDescription}</p>
        <dl className="company-details">
          <div>
            <dt>{companyCopy.companyName}</dt>
            <dd>{user?.displayName || appCopy.notSet}</dd>
          </div>
          <div>
            <dt>{companyCopy.emailAddress}</dt>
            <dd>{user?.email || appCopy.notSet}</dd>
          </div>
        </dl>
        {user && (
          <div className="company-form">
            <PasswordChangeSection disabled={false} onChanged={onPasswordChanged} />
            <LogoutEverywhereSection onDone={onLoggedOutEverywhere} />
            <AccountDeletionSection disabled={false} onDeleted={onAccountDeleted} />
          </div>
        )}
      </>
    );
  if (section === 'voice')
    return (
      <>
        <h1>{copy.settingsPageTitle}</h1>
        <VoiceSettingsSection />
        <VoiceSignatureSection />
      </>
    );
  if (section === 'personal-memory')
    return (
      <>
        <h1>{companyCopy.personalMemory}</h1>
        <p className="company-description">{companyCopy.personalMemoryDescription}</p>
        <MemoriesSection open signedIn={Boolean(user)} />
      </>
    );
  return (
    <>
      <h1>{appCopy.settings}</h1>
      <p className="company-description">{appCopy.settingsDescription}</p>
      {!settings ? (
        <div role="status">
          {error ? (
            <>
              <p role="alert" className="company-error">
                {error}
              </p>
              <button className="company-secondary" onClick={() => void load()}>
                {appCopy.tryAgain}
              </button>
            </>
          ) : (
            companyCopy.loading
          )}
        </div>
      ) : (
        <form
          className="company-form"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <LanguagePicker
            value={settings.language ?? ''}
            initialValue={initialLanguage}
            disabled={busy}
            onChange={(value) => update('language', value)}
          />
          <TimezoneField
            value={settings.timezone ?? ''}
            disabled={busy}
            onChange={(value) => update('timezone', value)}
          />
          <PersonalityField
            value={settings.personality ?? ''}
            disabled={busy}
            onChange={(value) => update('personality', value)}
          />
          <ModelPicker />
          {onChooseTheme && <AppearanceField choice={themeChoice} onChoose={onChooseTheme} />}
          {error && (
            <p role="alert" className="company-error">
              {error}
            </p>
          )}
          <div className="company-form-actions">
            <button className="company-primary" disabled={busy}>
              {busy ? companyCopy.saving : appCopy.saveChanges}
            </button>
            {saved && <span role="status">{companyCopy.preferencesSaved}</span>}
          </div>
        </form>
      )}
    </>
  );
}
